"use client";

import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { ErrorAlert } from "@/components/dashboard/organization-editor/AlertDialogs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
interface RequirementsFormProps {
  projectId: string | null;
  selectedStructure: string;
  setSelectedStructure: (value: string) => void;
  organizationalStructures: {
    value: string;
    label: string;
    description: string;
    image: string;
  }[];
  onSave: () => void;
  onNext: (projectId: string) => void;
}
interface RequirementsFormValues {
  directorsCount: number;
  managersCount: number;
  teamMembersCount: number;
}
export function RequirementsForm({
  projectId,
  selectedStructure,
  setSelectedStructure,
  organizationalStructures,
  onSave,
  onNext
}: RequirementsFormProps) {
  const [userId, setUserId] = useState<string | null>(null);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [showSaveConfirmDialog, setShowSaveConfirmDialog] = useState(false);
  const [isStructureSaved, setIsStructureSaved] = useState(false);
  const [formData, setFormData] = useState<RequirementsFormValues | null>(null);
  const [validationMessage, setValidationMessage] = useState("");
  const form = useForm<RequirementsFormValues>({
    defaultValues: {
      directorsCount: 0,
      managersCount: 0,
      teamMembersCount: 0
    }
  });
  useEffect(() => {
    const getUser = async () => {
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      if (session?.user) {
        setUserId(session.user.id);
      }
    };
    getUser();
  }, []);
  useEffect(() => {
    const fetchRequirements = async () => {
      if (!projectId) {
        form.reset({
          directorsCount: 0,
          managersCount: 0,
          teamMembersCount: 0
        });
        setSelectedStructure('hierarchical');
        setIsStructureSaved(false);
        return;
      }
      try {
        const {
          data,
          error
        } = await supabase.from('project_requirements').select('*').eq('id', projectId).maybeSingle();
        if (error) {
          console.error('Error fetching requirements:', error);
          return;
        }
        if (data) {
          form.reset({
            directorsCount: data.directors_count,
            managersCount: data.managers_count,
            teamMembersCount: data.team_members_count
          });
          setSelectedStructure(data.organizational_structure || 'hierarchical');
          setIsStructureSaved(data.is_saved || false);
        } else {
          form.reset({
            directorsCount: 0,
            managersCount: 0,
            teamMembersCount: 0
          });
          setIsStructureSaved(false);
        }
      } catch (error) {
        console.error('Error in fetchRequirements:', error);
        toast.error("Failed to fetch requirements");
      }
    };
    fetchRequirements();
  }, [projectId, form, setSelectedStructure]);
  const validateRequirements = (data: RequirementsFormValues): boolean => {
    if (data.directorsCount === 0 || data.managersCount === 0 || data.teamMembersCount === 0) {
      setValidationMessage("The number of Directors, Managers, and Team Members cannot be zero");
      return false;
    }
    return true;
  };
  const saveRequirements = async (data: RequirementsFormValues) => {
    if (!userId) {
      toast.error("You must be logged in to save requirements");
      return false;
    }
    try {
      const newProjectId = crypto.randomUUID();
      let projectData;
      if (projectId) {
        // Update existing project
        const {
          data: existingProject,
          error: updateError
        } = await supabase.from('project_requirements').update({
          directors_count: data.directorsCount,
          managers_count: data.managersCount,
          team_members_count: data.teamMembersCount,
          organizational_structure: selectedStructure,
          is_saved: true,
          project_id: newProjectId
        }).eq('id', projectId).select().maybeSingle();
        if (updateError) throw updateError;
        projectData = existingProject;
      } else {
        // Create new project
        const {
          data: newProject,
          error: insertError
        } = await supabase.from('project_requirements').insert({
          user_id: userId,
          directors_count: data.directorsCount,
          managers_count: data.managersCount,
          team_members_count: data.teamMembersCount,
          organizational_structure: selectedStructure,
          is_saved: true,
          project_id: newProjectId,
          name: 'Untitled Project'
        }).select().maybeSingle();
        if (insertError) throw insertError;
        projectData = newProject;
      }
      if (!projectData) {
        throw new Error('Failed to create/update project');
      }

      // Initialize organization structure with the same project_id
      const {
        error: orgStructError
      } = await supabase.from('organization_structures').insert({
        project_id: newProjectId,
        user_id: userId,
        name: projectData.name,
        nodes: [],
        edges: [],
        is_saved: false
      });
      if (orgStructError) {
        console.error('Error updating organization structure:', orgStructError);
        toast.error("Failed to update organization structure");
        return false;
      }
      setIsStructureSaved(true);
      onSave();
      toast.success("Project requirements saved successfully");
      return projectData;
    } catch (error) {
      console.error('Error saving requirements:', error);
      toast.error("Failed to save requirements");
      return false;
    }
  };
  const handleFormSubmit = async (data: RequirementsFormValues) => {
    if (!validateRequirements(data)) {
      setShowValidationDialog(true);
      return;
    }
    setFormData(data);
    setShowSaveConfirmDialog(true);
  };
  const handleConfirmSave = async () => {
    if (formData) {
      const savedProject = await saveRequirements(formData);
      if (savedProject && savedProject.project_id) {
        setShowSaveConfirmDialog(false);
      }
    }
    setShowSaveConfirmDialog(false);
  };
  const NumberInput = ({
    value,
    onChange,
    label,
    minValue = 0,
    disabled = false
  }: {
    value: number;
    onChange: (value: number) => void;
    label: string;
    minValue?: number;
    disabled?: boolean;
  }) => <FormItem className="space-y-2">
      <div className="flex items-center justify-between">
        <FormLabel className="text-white text-xs text-left">{label}</FormLabel>
        <div className="flex items-center gap-2">
          <ToggleGroup type="single" className="flex items-center">
            <ToggleGroupItem value="minus" size="sm" onClick={() => !disabled && onChange(Math.max(minValue, value - 1))} className={`text-white hover:text-white ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`} disabled={disabled}>
              <Minus className="h-4 w-4" />
            </ToggleGroupItem>
          </ToggleGroup>
          <FormControl>
            <Input type="number" value={value} onChange={e => !disabled && onChange(Math.max(minValue, parseInt(e.target.value) || minValue))} className={`w-20 text-center ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`} min={minValue} disabled={disabled} />
          </FormControl>
          <ToggleGroup type="single" className="flex items-center">
            <ToggleGroupItem value="plus" size="sm" onClick={() => !disabled && onChange(value + 1)} className={`text-white hover:text-white ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`} disabled={disabled}>
              <Plus className="h-4 w-4" />
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>
    </FormItem>;
  return <div className="space-y-2 h-full flex flex-col">
      <div className="bg-secondary/50 backdrop-blur-lg border-2 border-primary rounded-lg shadow-lg shadow-purple-500/10 p-6 animate-fade-in flex-1 transition-all duration-300 hover:shadow-purple-500/20">
        <h3 className="text-xl font-semibold mb-0 py-4 text-white">Requirements</h3>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-4">
            <div className="mb-6">
              
              <FormLabel className="text-primary-light text-xs text-left block mb-2">Select the type of organization structure and click Save</FormLabel>
              <Select onValueChange={setSelectedStructure} value={selectedStructure} disabled={isStructureSaved}>
                <SelectTrigger className={`w-[200px] bg-black text-white text-[12px] border-primary/20 ${isStructureSaved ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <SelectValue placeholder="Select organization structure" />
                </SelectTrigger>
                <SelectContent className="bg-black text-white w-[200px] text-[12px] border-primary/20">
                  {organizationalStructures.map(structure => <SelectItem key={structure.value} value={structure.value} className="text-white bg-black w-[200px] text-[12px]">
                      {structure.label}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              {isStructureSaved && <p className="text-yellow-500 text-xs mt-2">
                  Requirements for this project has been saved and frozen. If needed, create a new project with new requirements.
                </p>}
            </div>

            <FormField control={form.control} name="directorsCount" render={({
            field
          }) => <NumberInput value={field.value} onChange={field.onChange} label="Number of Directors" minValue={0} disabled={isStructureSaved} />} />

            <FormField control={form.control} name="managersCount" render={({
            field
          }) => <NumberInput value={field.value} onChange={field.onChange} label="Number of Managers" minValue={0} disabled={isStructureSaved} />} />

            <div className="mb-0">
              <FormField control={form.control} name="teamMembersCount" render={({
              field
            }) => <NumberInput value={field.value} onChange={field.onChange} label="Number of Team Members" minValue={0} disabled={isStructureSaved} />} />
            </div>
          </form>
        </Form>
      </div>

      <ErrorAlert open={showValidationDialog} onOpenChange={setShowValidationDialog} title="Invalid Requirements" description={validationMessage} />

      <AlertDialog open={showSaveConfirmDialog} onOpenChange={setShowSaveConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Save</AlertDialogTitle>
            <AlertDialogDescription>
              Project requirements will be frozen after saving. Confirm?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmSave}>Save</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>;
}